import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ShoppingCart, Heart, Star, CheckCircle, ExternalLink, User, Store, Play, Percent, MessageSquare, Gift, X, Sparkles, RefreshCcw, Calendar, Loader2, Share2, BadgeCheck, Monitor, Plug, Target, Clock, Copy, Check, Mail, Link2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import VideoPlayer from '@/components/VideoPlayer';
import { useTranslation } from 'react-i18next';
import SEOHead, { generateProductSchema } from '@/components/SEOHead';
import WishlistButton from '@/components/WishlistButton';
import { getCart as getStoredCart, setCart as saveCart } from '@/utils/cartStorage';
import ProductReviews from '@/components/ProductReviews';
import { getImageUrl } from '@/utils/imageUtils';
import ResilientImg from '@/components/ResilientImg';
import { PartnerBadge, isPartnerSeller } from '@/components/PartnerBadge';
import { VerifiedSellerBadge } from '@/components/VerifiedSellerBadge';
import { ShareProductModal } from '@/components/ShareProductModal';
import { RecentSalesBadge } from '@/components/RecentSalesBadge';

function ProductDetail() {
  const { t } = useTranslation();
  const { productId } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [creator, setCreator] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Distinguishes "still loading" from "the product genuinely doesn't
  // exist anymore" so we can render a friendly not-found state instead
  // of a blank black page (which is what buyers used to see when a
  // review-request email pointed at a deleted product id).
  const [notFound, setNotFound] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [affiliateProgram, setAffiliateProgram] = useState(null);
  const [upsells, setUpsells] = useState([]);
  const [showUpsellModal, setShowUpsellModal] = useState(false);
  const [selectedUpsell, setSelectedUpsell] = useState(null);
  // Subscription states
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const [selectedInterval, setSelectedInterval] = useState('monthly');
  const [subscribing, setSubscribing] = useState(false);
  // Share dropdown
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  
  // Check if came from a store
  const fromStore = location.state?.fromStore || searchParams.get('from') === 'store';
  const storeSlug = location.state?.storeSlug || searchParams.get('store');
  const storeName = location.state?.storeName;
  const refCode = searchParams.get('ref'); // Affiliate referral code
  
  // Track if we have creator info for back button
  const [hasCreatorInfo, setHasCreatorInfo] = useState(false);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
    updateCartCount();
    loadProduct();
    
    // Track affiliate click if ref code present
    if (refCode) {
      trackAffiliateClick(refCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, refCode]);

  const updateCartCount = useCallback(() => {
    const cart = getStoredCart();
    const count = cart.reduce((sum, item) => sum + (item.quantity || 1), 0);
    setCartCount(count);
  }, []);

  const trackAffiliateClick = useCallback(async (code) => {
    try {
      await axios.get(`/affiliate/track/${code}`);
    } catch (error) {
      console.error('Failed to track affiliate click:', error);
    }
  }, []);

  const loadProduct = async () => {
    try {
      setLoading(true);
      
      // Load product
      const productRes = await axios.get(`/products/${productId}`);
      setProduct(productRes.data);

      // Track link click for creator analytics
      try {
        const params = new URLSearchParams(window.location.search);
        await axios.post('/track/pageview', {
          page_type: 'product',
          page_id: productId,
          referrer: document.referrer || '',
          utm_source: params.get('utm_source') || '',
          utm_medium: params.get('utm_medium') || '',
          utm_campaign: params.get('utm_campaign') || '',
        });
      } catch (err) { console.error('Click tracking failed:', err); }
      
      // Track product view for retargeting (only for logged-in users)
      const token = localStorage.getItem('token');
      if (token) {
        try {
          await axios.post('/retargeting/track-view', 
            { product_id: productId },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        } catch (e) {
          // Retargeting is best-effort — log for debuggability, don't surface.
          console.debug('Retargeting track-view failed (non-fatal):', e?.message || e);
        }
      }
      
      // Load creator info - always try to get creator details for "Back to Store" button
      try {
        const creatorId = productRes.data.creator_id;
        // Get creator by ID (new endpoint)
        const creatorInfoRes = await axios.get(`/creators/by-id/${creatorId}`).catch(() => null);
        if (creatorInfoRes?.data) {
          setCreator(creatorInfoRes.data);
          setHasCreatorInfo(true);
        }
      } catch (err) {
        console.error('Could not load creator:', err);
      }
      
      // Load affiliate program
      try {
        const affiliateRes = await axios.get(`/creators/${productRes.data.creator_id}/affiliate-programs`);
        const program = affiliateRes.data.find(p => p.product_id === productId && p.is_active);
        setAffiliateProgram(program);
      } catch (err) {
        console.error('Could not load affiliate program:', err);
      }
      
      // Load reviews
      const reviewsRes = await axios.get(`/reviews/${productId}`);
      setReviews(reviewsRes.data);
      
      // Load related products — STRICT same-category match. If the product
      // has no category_id, skip the lookup entirely (otherwise an empty
      // query param could pull the whole catalog).
      const productCategoryId = productRes.data.category_id;
      if (productCategoryId) {
        const relatedRes = await axios.get(`/products?category_id=${encodeURIComponent(productCategoryId)}`);
        setRelatedProducts(relatedRes.data.filter(p => p.product_id !== productId).slice(0, 4));
      } else {
        setRelatedProducts([]);
      }
      
      // Load upsells for this product (includes auto-suggested if no manual upsells)
      try {
        const upsellsRes = await axios.get(`/products/${productId}/upsells?include_auto=true`);
        const allUpsells = [
          ...(upsellsRes.data.upsells || []),
          ...(upsellsRes.data.auto_suggested || [])
        ];
        setUpsells(allUpsells);
      } catch (e) {
        // No upsells configured for this product — non-fatal. Log for
        // debuggability but don't surface (legitimate empty state).
        console.debug('No upsells available for product:', productId, e?.message);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading product:', error);
      // 404 → product was deleted. Render the friendly not-found
      // panel below instead of a toast on a black void.
      if (error?.response?.status === 404) {
        setNotFound(true);
      } else {
        toast.error('Failed to load product');
      }
      setLoading(false);
    }
  };

  const addToCart = () => {
    const cart = getStoredCart();
    const existingItem = cart.find(item => item.product_id === productId);
    
    // For subscription products, the cart line is a one-time/lifetime
    // purchase — it must use one_time_price, NOT the monthly rate.
    // Server re-verifies on checkout, but matching the display value
    // prevents a confusing "shown $228, charged $228" vs "shown $19" gap.
    const effectiveOneTime = product.is_subscription
      ? (product.one_time_price ?? Math.round((product.price || 0) * 12))
      : product.price;

    if (existingItem) {
      existingItem.quantity += 1;
      toast.success(`Updated quantity! (${existingItem.quantity} in cart)`);
    } else {
      cart.push({ 
        ...product, 
        price: effectiveOneTime,
        quantity: 1,
        affiliate_ref: refCode || null // Track affiliate referral
      });
      toast.success('Added to cart!');
    }
    
    saveCart(cart);
    updateCartCount();
    
    // Dispatch custom event for cart update
    window.dispatchEvent(new Event('cartUpdated'));
    
    // Show upsell modal if there are upsells with position "product" or "cart"
    const productUpsells = upsells.filter(u => u.position === 'product' || u.position === 'cart');
    if (productUpsells.length > 0) {
      setSelectedUpsell(productUpsells[0]);
      setShowUpsellModal(true);
    }
  };

  const addUpsellToCart = (upsell) => {
    const cart = getStoredCart();
    const upsellProduct = upsell.upsell_product;
    
    const newItem = {
      product_id: upsell.upsell_product_id,
      name: upsellProduct.name,
      price: upsellProduct.discounted_price,
      original_price: upsellProduct.original_price,
      images: upsellProduct.images,
      description: upsellProduct.description,
      quantity: 1,
      is_upsell: true,
      upsell_discount: upsell.discount_percent,
      affiliate_ref: refCode || null // Inherit affiliate ref from original product
    };
    
    cart.push(newItem);
    saveCart(cart);
    updateCartCount();
    window.dispatchEvent(new Event('cartUpdated'));
    
    setShowUpsellModal(false);
    toast.success(`Added ${upsellProduct.name} to cart with ${upsell.discount_percent}% discount!`);
  };

  const handleSubscribe = async () => {
    if (!user) {
      toast.error('Please sign in to subscribe');
      return;
    }

    setSubscribing(true);
    try {
      const response = await axios.post('/subscriptions/subscribe', {
        product_id: productId,
        interval: selectedInterval
      });

      if (response.data.client_secret) {
        // Redirect to Stripe for payment - get key from backend
        const keyResponse = await axios.get('/stripe/publishable-key');
        const stripe = await import('@stripe/stripe-js').then(m => 
          m.loadStripe(keyResponse.data.publishable_key)
        );
        
        const { error } = await stripe.confirmCardPayment(response.data.client_secret);
        
        if (error) {
          toast.error(error.message);
        } else {
          toast.success('Subscription started successfully!');
          setShowSubscribeModal(false);
          navigate('/my-subscriptions');
        }
      } else {
        // Trial subscription started without payment
        toast.success('Subscription started! Your free trial has begun.');
        setShowSubscribeModal(false);
        navigate('/my-subscriptions');
      }
    } catch (error) {
      console.error('Subscription error:', error);
      toast.error(error.response?.data?.detail || 'Failed to start subscription');
    } finally {
      setSubscribing(false);
    }
  };

  const goToCart = () => {
    navigate('/checkout');
  };

  const handleBackClick = () => {
    // Always go to creator's store if we have creator info
    if (creator?.store_slug) {
      navigate(`/store/${creator.store_slug}`);
    } else if (fromStore && storeSlug) {
      navigate(`/store/${storeSlug}`);
    } else {
      navigate('/');
    }
  };

  if (notFound) {
    return (
      <div
        className="min-h-screen bg-[#050505] text-white flex items-center justify-center p-6"
        data-testid="product-not-found"
      >
        {/* Tell Google this is a soft-404 so it stops indexing the deleted
            product URL (Search Console "Soft 404" fix, Feb 2026). Our SPA
            can't return a real 404 status; noindex is the recommended
            equivalent signal. */}
        <SEOHead
          title="Product not found"
          description="This product is no longer available on theailedger.ai."
          path={`/product/${productId}`}
          noindex
        />
        <div className="max-w-md text-center">
          <div className="text-6xl mb-4">⌁</div>
          <h1 className="text-2xl font-bold mb-2">This product is no longer available</h1>
          <p className="text-neutral-400 text-sm mb-6 leading-relaxed">
            The product you were trying to view has been removed from the marketplace.
            If you arrived here from a review-request email, the creator may have taken
            this product down — your purchase still counts, and you can still leave a
            review for other items in your order from your{' '}
            <button
              onClick={() => navigate('/orders')}
              className="text-[#00F0FF] underline-offset-2 hover:underline"
            >
              order history
            </button>.
          </p>
          <Button
            onClick={() => navigate('/marketplace')}
            className="bg-[#00F0FF] hover:bg-[#00C2CC] text-black font-semibold rounded-full px-6"
            data-testid="back-to-marketplace-btn"
          >
            Back to marketplace
          </Button>
        </div>
      </div>
    );
  }

  if (loading || !product) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* SEO Head */}
      {product && (
        <SEOHead 
          title={product.name}
          description={product.description?.substring(0, 160)}
          path={`/product/${productId}`}
          image={product?.slug
            ? `https://theailedger.ai/products/og/${product.slug}.jpg`
            : (product.images?.[0] || undefined)}
          type="product"
          schema={generateProductSchema(product, `https://theailedger.ai/product/${product?.slug || productId}`)}
        />
      )}
      {/* Header */}
      <header className="border-b border-white/10 backdrop-blur-xl bg-black/40 sticky top-0 z-50">
        <div className="container mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={handleBackClick}
            className="text-[#00F0FF] hover:text-[#00C2CC]"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            {creator?.store_slug ? (
              <>
                <Store className="w-4 h-4 mr-1" />
                {t('product.backToStore')} {creator.store_name || storeName || 'Store'}
              </>
            ) : (
              t('product.backToMarketplace')
            )}
          </Button>
          
          {/* Cart Button */}
          <Button
            variant="outline"
            onClick={goToCart}
            className="border-white/10 hover:bg-white/5 relative"
          >
            <ShoppingCart className="w-5 h-5" />
            {cartCount > 0 && (
              <span className="absolute -top-2 -right-2 w-5 h-5 bg-[#00F0FF] text-black text-xs font-bold rounded-full flex items-center justify-center">
                {cartCount}
              </span>
            )}
          </Button>
        </div>
      </header>

      {/* Affiliate Banner - Only show when visitor came through affiliate link */}
      {affiliateProgram && refCode && (
        <div className="bg-gradient-to-r from-[#00F0FF]/10 to-[#00F0FF]/10 border-b border-[#00F0FF]/20">
          <div className="container mx-auto px-6 md:px-12 py-3 flex items-center justify-center gap-3">
            <Percent className="w-5 h-5 text-[#00F0FF]" />
            <span className="text-sm">
              <span className="text-neutral-400">{t('product.referredByAffiliate')}</span>
            </span>
          </div>
        </div>
      )}

      {/* Product Detail */}
      <div className="container mx-auto px-6 md:px-12 py-12">
        {/* Video Demo Section - Full Width Above Product Details */}
        {product.video_demo_url && (
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <Play className="w-6 h-6 text-white" />
              <h2 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {t('product.watchDemo')}
              </h2>
            </div>
            <VideoPlayer videoUrl={product.video_demo_url} productName={product.name} />
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          {/* Product Image */}
          <div className="relative">
            <div className="aspect-square rounded-2xl overflow-hidden border border-white/10 bg-neutral-900">
              <ResilientImg 
                src={product.images[0]?.startsWith('/api') 
                  ? `${process.env.REACT_APP_BACKEND_URL}${product.images[0]}` 
                  : product.images[0]
                } 
                alt={product.name} 
                className="w-full h-full object-cover"
                data-testid="product-image"
              />
            </div>
            
            {/* Thumbnail Gallery */}
            {product.images.length > 1 && (
              <div className="flex gap-3 mt-4">
                {product.images.slice(0, 4).map((img, idx) => (
                  <div 
                    key={`img-${idx}-${img.slice(-20)}`}
                    className="w-20 h-20 rounded-lg overflow-hidden border border-white/10 cursor-pointer hover:border-[#00F0FF]/50"
                  >
                    <ResilientImg 
                      src={img.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${img}` : img}
                      alt={`${product.name} ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="flex flex-col">
            <h1 className="text-4xl font-black mb-4" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="product-title">
              {product.name}
              {product.verified && (
                <span className="inline-flex items-center gap-1.5 ml-3 px-3 py-1 rounded-full bg-[#00F0FF]/10 border border-[#00F0FF]/30 align-middle" data-testid="verified-badge">
                  <BadgeCheck className="w-4 h-4 text-[#00F0FF]" />
                  <span className="text-xs font-semibold text-[#00F0FF] tracking-wide">VERIFIED</span>
                </span>
              )}
            </h1>
            
            {/* Creator Link */}
            {creator && (
              <div 
                onClick={() => navigate(`/store/${creator.store_slug}`)}
                className="flex items-center gap-2 mb-4 cursor-pointer hover:text-[#00F0FF] transition-colors"
              >
                <Store className="w-4 h-4 text-neutral-400" />
                <span className="text-neutral-400">by</span>
                <span className="font-semibold">{creator.store_name}</span>
                {isPartnerSeller(creator) && (
                  <PartnerBadge size="sm" className="ml-1" />
                )}
                <VerifiedSellerBadge verified={product.is_verified_seller} size="md" className="ml-1" />
              </div>
            )}
            
            <div className="flex items-center gap-4 mb-6">
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5 fill-[#00F0FF] text-[#00F0FF]" />
                <span className="text-xl font-bold">{product.rating}</span>
                <span className="text-neutral-400">({product.total_reviews} {t('product.reviews')})</span>
              </div>
              <span className="text-neutral-500">|</span>
              <span className="text-neutral-400">{product.total_sales} {t('product.sales')}</span>
            </div>

            <p className="text-lg text-neutral-300 mb-8 leading-relaxed" data-testid="product-description">
              {product.description}
            </p>

            {/* Pricing Section - One-time vs Subscription */}
            {product.is_subscription ? (
              <div className="mb-8">
                {/* Subscription Pricing */}
                <div className="bg-gradient-to-br from-[#00FF94]/10 to-[#00FF94]/5 border border-[#00FF94]/30 rounded-xl p-6 mb-4">
                  <div className="flex items-center gap-2 mb-4">
                    <RefreshCcw className="w-5 h-5 text-[#00FF94]" />
                    <span className="font-semibold text-[#00FF94]">Subscription Available</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div 
                      onClick={() => setSelectedInterval('monthly')}
                      className={`p-4 rounded-xl border cursor-pointer transition-all ${
                        selectedInterval === 'monthly' 
                          ? 'border-[#00F0FF] bg-[#00F0FF]/10' 
                          : 'border-white/10 hover:border-white/30'
                      }`}
                    >
                      <p className="text-sm text-neutral-400">Monthly</p>
                      <p className="text-2xl font-bold text-[#00F0FF]">${product.price}<span className="text-sm font-normal">/mo</span></p>
                    </div>
                    <div 
                      onClick={() => setSelectedInterval('yearly')}
                      className={`p-4 rounded-xl border cursor-pointer transition-all relative ${
                        selectedInterval === 'yearly' 
                          ? 'border-white bg-white text-black/10' 
                          : 'border-white/10 hover:border-white/30'
                      }`}
                    >
                      <span className="absolute -top-2 -right-2 bg-green-500 text-black text-xs font-bold px-2 py-0.5 rounded-full">
                        Save {Math.round((1 - ((product.yearly_price || product.price * 10) / (product.price * 12))) * 100)}%
                      </span>
                      <p className="text-sm text-neutral-400">Annual</p>
                      <p className="text-2xl font-bold text-white">${(product.yearly_price || product.price * 10).toFixed(2)}<span className="text-sm font-normal">/yr</span></p>
                    </div>
                  </div>
                  {product.trial_days > 0 && (
                    <div className="mt-4 flex items-center gap-2 text-sm text-[#00F0FF]">
                      <Calendar className="w-4 h-4" />
                      <span>{product.trial_days} day free trial included</span>
                    </div>
                  )}
                </div>

                {/* Subscribe Button */}
                <Button
                  onClick={() => setShowSubscribeModal(true)}
                  className="w-full bg-[#00FF94] hover:bg-[#00CC75] text-black rounded-full py-6 text-lg font-bold mb-4"
                >
                  <RefreshCcw className="w-5 h-5 mr-2" />
                  Subscribe {selectedInterval === 'monthly' ? `$${product.price}/mo` : `$${(product.yearly_price || product.price * 10).toFixed(2)}/yr`}
                </Button>

                {/* Or Buy One-time */}
                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-white/10"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-[#050505] text-neutral-500">or purchase one-time</span>
                  </div>
                </div>

                <div className="flex items-baseline gap-3 mb-4">
                  <span className="text-3xl font-black text-[#00F0FF]" data-testid="product-one-time-price">
                    ${(product.one_time_price ?? Math.round((product.price || 0) * 12)).toFixed(2)}
                  </span>
                  <span className="text-neutral-500">{t('product.oneTimePayment')}</span>
                </div>
              </div>
            ) : (
              <div className="flex items-baseline gap-3 mb-8">
                <span className="text-5xl font-black text-[#00F0FF]" data-testid="product-price">
                  ${product.price}
                </span>
                <span className="text-neutral-500">{t('product.oneTimePayment')}</span>
              </div>
            )}

            <div className="flex gap-4 mb-2">
              <Button
                onClick={addToCart}
                className="flex-1 bg-white hover:bg-neutral-200 text-black rounded-full py-6 text-lg font-bold"
                data-testid="add-to-cart-button"
              >
                <ShoppingCart className="w-5 h-5 mr-2" />
                {t('product.addToCart')}
              </Button>
              <WishlistButton productId={productId} size="default" />
              <div className="relative">
                <Button
                  onClick={() => setShowShareMenu(!showShareMenu)}
                  variant="outline"
                  className="border-[#00F0FF]/30 hover:bg-[#00F0FF]/10 text-[#00F0FF] rounded-full p-3"
                  title="Share Product"
                  data-testid="share-product-btn"
                >
                  <Share2 className="w-5 h-5" />
                </Button>
                {showShareMenu && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowShareMenu(false)} />
                    <div className="absolute right-0 top-full mt-2 w-56 bg-neutral-900 border border-white/10 rounded-xl shadow-2xl z-50 overflow-hidden" data-testid="share-menu-dropdown">
                      <div className="p-3 border-b border-white/5">
                        <p className="text-white text-xs font-bold mb-1">Share this product</p>
                        <p className="text-neutral-500 text-[10px] truncate">{product?.name}</p>
                      </div>
                      <div className="p-1.5">
                        <button
                          onClick={() => {
                            const url = `https://theailedger.ai/product/${product?.slug || productId}`;
                            try { navigator.clipboard.writeText(url); } catch(err) { const t = document.createElement('textarea'); t.value = url; document.body.appendChild(t); t.select(); document.execCommand('copy'); document.body.removeChild(t); }
                            setLinkCopied(true);
                            toast.success('Product link copied!');
                            setTimeout(() => { setLinkCopied(false); setShowShareMenu(false); }, 1500);
                          }}
                          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white/5 transition-colors text-left"
                          data-testid="share-copy-link"
                        >
                          {linkCopied ? <Check className="w-4 h-4 text-green-400 shrink-0" /> : <Copy className="w-4 h-4 text-neutral-400 shrink-0" />}
                          <span className="text-sm text-neutral-200">{linkCopied ? 'Copied!' : 'Copy Link'}</span>
                        </button>
                        <button
                          onClick={() => {
                            const url = encodeURIComponent(`https://theailedger.ai/product/${product?.slug || productId}`);
                            const text = encodeURIComponent(`Check out "${product?.name}" on The AI Ledger!`);
                            window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, '_blank');
                            setShowShareMenu(false);
                          }}
                          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white/5 transition-colors text-left"
                          data-testid="share-twitter"
                        >
                          <svg className="w-4 h-4 text-[#1DA1F2] shrink-0" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                          <span className="text-sm text-neutral-200">Share on X</span>
                        </button>
                        <button
                          onClick={() => {
                            const url = `https://theailedger.ai/product/${product?.slug || productId}`;
                            const text = encodeURIComponent(`Check out "${product?.name}" on The AI Ledger! ${url}`);
                            window.open(`https://wa.me/?text=${text}`, '_blank');
                            setShowShareMenu(false);
                          }}
                          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white/5 transition-colors text-left"
                          data-testid="share-whatsapp"
                        >
                          <svg className="w-4 h-4 text-[#25D366] shrink-0" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                          <span className="text-sm text-neutral-200">Share on WhatsApp</span>
                        </button>
                        <button
                          onClick={() => {
                            const url = `https://theailedger.ai/product/${product?.slug || productId}`;
                            const subject = encodeURIComponent(`Check out ${product?.name} on The AI Ledger`);
                            const body = encodeURIComponent(`I found this AI tool on The AI Ledger and thought you'd be interested:\n\n${product?.name} — $${product?.price}\n${product?.description?.slice(0, 150)}...\n\n${url}`);
                            window.open(`mailto:?subject=${subject}&body=${body}`);
                            setShowShareMenu(false);
                          }}
                          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white/5 transition-colors text-left"
                          data-testid="share-email"
                        >
                          <Mail className="w-4 h-4 text-neutral-400 shrink-0" />
                          <span className="text-sm text-neutral-200">Share via Email</span>
                        </button>
                        <button
                          onClick={() => {
                            sessionStorage.setItem('shareProduct', JSON.stringify({
                              product_id: productId,
                              name: product.name,
                              price: product.price,
                              image: product.images?.[0]
                            }));
                            navigate('/chat/room_deals_discounts');
                            toast.success('Opening Deals & Discounts chat to share this product');
                            setShowShareMenu(false);
                          }}
                          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white/5 transition-colors text-left"
                          data-testid="share-chat"
                        >
                          <MessageSquare className="w-4 h-4 text-white shrink-0" />
                          <span className="text-sm text-neutral-200">Share in Chat</span>
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Buy Now Button */}
            <Button
              onClick={() => {
                addToCart();
                navigate('/checkout');
              }}
              className="w-full bg-[#00F0FF] hover:bg-[#00C2CC] text-black rounded-full py-6 text-lg font-bold mb-4"
            >
              {t('product.buyNow')}
            </Button>

            {/* Recent-sales social proof — renders ONLY when this
                product has 3+ real (non-seeded) purchases in the
                last 30 days. Dead products stay quiet. */}
            <RecentSalesBadge productId={productId} />

            {/* Share button — opens modal with one-click X / Threads / LinkedIn /
                Facebook posts plus an Instagram copy helper. Each share URL
                gets a tracking ref so attributed traffic and sales surface in
                the admin "Top Sharers" board. Always visible for everyone. */}
            <Button
              onClick={() => setShowShareModal(true)}
              variant="outline"
              className="w-full border-white/15 hover:bg-white/5 text-white rounded-full py-5 text-base font-semibold mb-4"
              data-testid="open-share-modal-btn"
            >
              <Share2 className="w-4 h-4 mr-2" />
              Share this product
            </Button>

            {/* Message Seller Button - Always visible */}
            {creator && (
              <>
                {user && user.user_id !== creator.user_id ? (
                  <Button
                    onClick={() => navigate(`/messages?to=${creator.user_id}`)}
                    variant="outline"
                    className="w-full border-[#00F0FF]/30 hover:bg-[#00F0FF]/10 text-[#00F0FF] rounded-full py-6 text-lg font-bold mb-8"
                    data-testid="message-creator-btn"
                  >
                    <MessageSquare className="w-5 h-5 mr-2" />
                    {t('product.askSeller')}
                  </Button>
                ) : !user && (
                  <Button
                    onClick={() => toast.info('Sign in to message this creator directly')}
                    variant="outline"
                    className="w-full border-[#00F0FF]/30 hover:bg-[#00F0FF]/10 text-[#00F0FF] rounded-full py-6 text-lg font-bold mb-4"
                    data-testid="message-creator-signin-btn"
                  >
                    <MessageSquare className="w-5 h-5 mr-2" />
                    Message {creator.store_name}
                  </Button>
                )}
              </>
            )}

            {/* Show login prompt if not logged in */}
            {!user && (
              <p className="text-center text-neutral-400 text-sm mb-8">
                <span className="text-[#00F0FF] cursor-pointer hover:underline" onClick={() => toast.info(t('product.signInToAsk'))}>
                  {t('auth.signIn')}
                </span> to message the creator or purchase this product
              </p>
            )}

            {/* Features */}
            <div className="bg-neutral-900/50 border border-white/10 rounded-xl p-6">
              <h3 className="text-xl font-bold mb-4">{t('product.keyFeatures')}</h3>
              <ul className="space-y-3">
                {product.features.map((feature, idx) => (
                  <li key={`feature-${idx}-${feature.slice(0, 15).replace(/\W/g, '')}`} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#00FF94] flex-shrink-0 mt-0.5" />
                    <span className="text-neutral-300">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            {product.demo_url && (
              <Button
                variant="outline"
                className="mt-4 border-white/10 hover:bg-white/5 rounded-full"
                onClick={() => window.open(product.demo_url, '_blank')}
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                {t('product.viewDemo')}
              </Button>
            )}
          </div>
        </div>

        {/* Tabs Section */}
        <Tabs defaultValue="reviews" className="mb-16">
          <TabsList className="bg-neutral-900/50 border border-white/10 p-2 rounded-full mb-8 flex-wrap">
            <TabsTrigger value="reviews" className="rounded-full px-8 data-[state=active]:bg-[#00F0FF] data-[state=active]:text-black">
              {t('product.reviewsTab')} ({reviews.length})
            </TabsTrigger>
            <TabsTrigger value="details" className="rounded-full px-8 data-[state=active]:bg-[#00F0FF] data-[state=active]:text-black">
              {t('product.detailsTab')}
            </TabsTrigger>
            <TabsTrigger value="policy" className="rounded-full px-8 data-[state=active]:bg-[#00F0FF] data-[state=active]:text-black">
              Seller Policy
            </TabsTrigger>
          </TabsList>

          <TabsContent value="reviews">
            <ProductReviews productId={productId} />
          </TabsContent>

          <TabsContent value="details">
            <div className="bg-neutral-900/50 border border-white/10 rounded-xl p-8">
              <h3 className="text-2xl font-bold mb-6">{t('product.productDetails')}</h3>

              {/* Standardized Specs Grid */}
              {product.specs && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                  <div className="bg-neutral-800/50 rounded-xl p-4 border border-white/5">
                    <div className="flex items-center gap-2 mb-2">
                      <Monitor className="w-4 h-4 text-[#00F0FF]" />
                      <span className="text-xs uppercase tracking-wider text-neutral-500 font-semibold">Platform</span>
                    </div>
                    <p className="text-white font-semibold text-sm">{product.specs.platform}</p>
                  </div>
                  <div className="bg-neutral-800/50 rounded-xl p-4 border border-white/5">
                    <div className="flex items-center gap-2 mb-2">
                      <Plug className="w-4 h-4 text-white" />
                      <span className="text-xs uppercase tracking-wider text-neutral-500 font-semibold">Integration</span>
                    </div>
                    <p className="text-white font-semibold text-sm">{product.specs.integration_type}</p>
                  </div>
                  <div className="bg-neutral-800/50 rounded-xl p-4 border border-white/5">
                    <div className="flex items-center gap-2 mb-2">
                      <Target className="w-4 h-4 text-[#00FF94]" />
                      <span className="text-xs uppercase tracking-wider text-neutral-500 font-semibold">Use Case</span>
                    </div>
                    <p className="text-white font-semibold text-sm">{product.specs.use_case}</p>
                  </div>
                  <div className="bg-neutral-800/50 rounded-xl p-4 border border-white/5">
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="w-4 h-4 text-amber-400" />
                      <span className="text-xs uppercase tracking-wider text-neutral-500 font-semibold">Last Updated</span>
                    </div>
                    <p className="text-white font-semibold text-sm">{new Date(product.specs.last_updated).toLocaleDateString('en-US', { year: 'numeric', month: 'short' })}</p>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4 py-3 border-b border-white/5">
                  <span className="text-neutral-400">{t('product.productId')}</span>
                  <span className="col-span-2 font-mono text-sm">{product.product_id}</span>
                </div>
                <div className="grid grid-cols-3 gap-4 py-3 border-b border-white/5">
                  <span className="text-neutral-400">{t('product.category')}</span>
                  <span className="col-span-2">{product.category || product.category_id.replace('cat_', '').replace('_', ' ').toUpperCase()}</span>
                </div>
                <div className="grid grid-cols-3 gap-4 py-3 border-b border-white/5">
                  <span className="text-neutral-400">{t('product.released')}</span>
                  <span className="col-span-2">{new Date(product.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                </div>
                <div className="grid grid-cols-3 gap-4 py-3 border-b border-white/5">
                  <span className="text-neutral-400">Quality Status</span>
                  <span className="col-span-2 flex items-center gap-2">
                    {product.verified ? (
                      <>
                        <BadgeCheck className="w-4 h-4 text-[#00F0FF]" />
                        <span className="text-[#00F0FF] font-semibold">Verified Quality</span>
                      </>
                    ) : (
                      <span className="text-neutral-400">Pending Verification</span>
                    )}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-4 py-3">
                  <span className="text-neutral-400">{t('product.totalSales')}</span>
                  <span className="col-span-2 font-bold text-[#00FF94]">{product.total_sales} {t('product.units')}</span>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="policy">
            <div className="bg-neutral-900/50 border border-white/10 rounded-xl p-8">
              <h3 className="text-2xl font-bold mb-6 flex items-center gap-2">
                <Store className="w-6 h-6 text-[#00F0FF]" />
                Seller Policy
              </h3>
              
              {/* Refund Policy */}
              <div className="mb-8">
                <h4 className="text-lg font-semibold mb-4 text-amber-400">Refund Policy</h4>
                <div className="bg-neutral-800/50 rounded-lg p-6 border border-amber-500/20">
                  {creator?.refund_policy ? (
                    <p className="text-neutral-300 whitespace-pre-wrap leading-relaxed">{creator.refund_policy}</p>
                  ) : (
                    <p className="text-neutral-400 italic">
                      All sales are final. Refund requests are handled on a case-by-case basis. 
                      Please contact the seller for support.
                    </p>
                  )}
                </div>
              </div>

              {/* Contact Seller */}
              {creator && (
                <div className="bg-neutral-800/50 rounded-lg p-6 border border-white/10">
                  <h4 className="text-lg font-semibold mb-3">Questions about this product?</h4>
                  <p className="text-neutral-400 text-sm mb-4">
                    Contact {creator.store_name} directly for support or questions about their products and policies.
                  </p>
                  {user && user.user_id !== creator.user_id && (
                    <Button
                      onClick={() => navigate(`/messages?to=${creator.user_id}`)}
                      variant="outline"
                      className="border-[#00F0FF]/30 hover:bg-[#00F0FF]/10 text-[#00F0FF]"
                    >
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Message Seller
                    </Button>
                  )}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div>
            <h2 className="text-3xl font-bold mb-8" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {t('product.relatedProducts')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedProducts.map((relatedProduct) => (
                <div
                  key={relatedProduct.product_id}
                  className="group relative rounded-2xl bg-neutral-900/50 border border-white/5 hover:border-[#00F0FF]/50 overflow-hidden cursor-pointer card-hover"
                  onClick={() => navigate(`/product/${relatedProduct.product_id}`, {
                    state: fromStore ? { fromStore: true, storeSlug, storeName } : {}
                  })}
                >
                  <div className="aspect-video bg-neutral-800 overflow-hidden">
                    <img 
                      src={relatedProduct.images[0]?.startsWith('/api') 
                        ? `${process.env.REACT_APP_BACKEND_URL}${relatedProduct.images[0]}` 
                        : relatedProduct.images[0]
                      } 
                      alt={relatedProduct.name} 
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="font-bold text-lg mb-2 line-clamp-1">{relatedProduct.name}</h3>
                    <div className="flex items-center justify-between">
                      <span className="text-xl font-bold text-[#00F0FF]">${relatedProduct.price}</span>
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 fill-[#00F0FF] text-[#00F0FF]" />
                        <span className="text-sm">{relatedProduct.rating}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Upsell Modal */}
      <Dialog open={showUpsellModal} onOpenChange={setShowUpsellModal}>
        <DialogContent className="bg-neutral-900 border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#00F0FF]" />
              Special Offer!
            </DialogTitle>
          </DialogHeader>
          
          {selectedUpsell && (
            <div className="mt-4">
              <div className="bg-gradient-to-r from-[#00F0FF]/10 to-[#00F0FF]/10 border border-[#00F0FF]/30 rounded-xl p-4 mb-4">
                <p className="text-[#00F0FF] font-bold text-lg mb-2">{selectedUpsell.headline}</p>
                <p className="text-sm text-neutral-300">{selectedUpsell.description}</p>
              </div>
              
              <div className="bg-neutral-800/50 rounded-xl p-4 mb-6">
                <div className="flex gap-4">
                  {selectedUpsell.upsell_product.images?.[0] && (
                    <ResilientImg 
                      src={getImageUrl(selectedUpsell.upsell_product.images[0])}
                      alt={selectedUpsell.upsell_product.name}
                      className="w-20 h-20 rounded-lg object-cover"
                    />
                  )}
                  <div className="flex-1">
                    <h4 className="font-bold mb-1">{selectedUpsell.upsell_product.name}</h4>
                    <div className="flex items-center gap-2">
                      <span className="text-xl font-bold text-[#00F0FF]">
                        ${selectedUpsell.upsell_product.discounted_price.toFixed(2)}
                      </span>
                      {selectedUpsell.discount_percent > 0 && (
                        <span className="text-sm text-neutral-500 line-through">
                          ${selectedUpsell.upsell_product.original_price.toFixed(2)}
                        </span>
                      )}
                    </div>
                    {selectedUpsell.discount_percent > 0 && (
                      <span className="inline-block mt-2 text-xs bg-[#00F0FF]/20 text-[#00F0FF] px-2 py-1 rounded-full">
                        Save {selectedUpsell.discount_percent}%
                      </span>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="flex gap-3">
                <Button
                  onClick={() => addUpsellToCart(selectedUpsell)}
                  className="flex-1 bg-[#00F0FF] hover:bg-[#00C2CC] text-black rounded-full font-bold"
                >
                  <Gift className="w-4 h-4 mr-2" />
                  Yes, Add to Cart!
                </Button>
                <Button
                  onClick={() => setShowUpsellModal(false)}
                  variant="outline"
                  className="border-white/20 hover:bg-white/5 rounded-full"
                >
                  No Thanks
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Subscribe Modal */}
      <Dialog open={showSubscribeModal} onOpenChange={setShowSubscribeModal}>
        <DialogContent className="bg-neutral-900 border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <RefreshCcw className="w-5 h-5 text-[#00FF94]" />
              Subscribe to {product?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="mt-4 space-y-4">
            {/* Billing Cycle Selection */}
            <div className="grid grid-cols-2 gap-4">
              <div 
                onClick={() => setSelectedInterval('monthly')}
                className={`p-4 rounded-xl border cursor-pointer transition-all ${
                  selectedInterval === 'monthly' 
                    ? 'border-[#00F0FF] bg-[#00F0FF]/10' 
                    : 'border-white/10 hover:border-white/30'
                }`}
              >
                <p className="text-sm text-neutral-400">Monthly</p>
                <p className="text-2xl font-bold text-[#00F0FF]">${product?.price}<span className="text-sm font-normal">/mo</span></p>
              </div>
              <div 
                onClick={() => setSelectedInterval('yearly')}
                className={`p-4 rounded-xl border cursor-pointer transition-all relative ${
                  selectedInterval === 'yearly' 
                    ? 'border-white bg-white text-black/10' 
                    : 'border-white/10 hover:border-white/30'
                }`}
              >
                <span className="absolute -top-2 -right-2 bg-green-500 text-black text-xs font-bold px-2 py-0.5 rounded-full">
                  Best Value
                </span>
                <p className="text-sm text-neutral-400">Annual</p>
                <p className="text-2xl font-bold text-white">${(product?.yearly_price || product?.price * 10).toFixed(2)}<span className="text-sm font-normal">/yr</span></p>
              </div>
            </div>

            {/* Trial Info */}
            {product?.trial_days > 0 && (
              <div className="bg-[#00F0FF]/10 border border-[#00F0FF]/30 rounded-xl p-4 flex items-center gap-3">
                <Calendar className="w-5 h-5 text-[#00F0FF]" />
                <div>
                  <p className="font-semibold text-[#00F0FF]">Free Trial Included</p>
                  <p className="text-sm text-neutral-400">
                    Start with a {product.trial_days} day free trial. Cancel anytime.
                  </p>
                </div>
              </div>
            )}

            {/* What's Included */}
            <div className="bg-neutral-800/50 rounded-xl p-4">
              <h4 className="font-semibold mb-3">What&apos;s included:</h4>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <span>Full access to {product?.name}</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <span>All future updates included</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <span>Cancel anytime</span>
                </li>
              </ul>
            </div>

            {/* Subscribe Button */}
            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setShowSubscribeModal(false)}
                className="flex-1 border-white/20"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubscribe}
                disabled={subscribing || !user}
                className="flex-1 bg-[#00FF94] hover:bg-[#00CC75] text-black font-bold"
              >
                {subscribing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <RefreshCcw className="w-4 h-4 mr-2" />
                    {product?.trial_days > 0 ? 'Start Free Trial' : 'Subscribe Now'}
                  </>
                )}
              </Button>
            </div>

            {!user && (
              <p className="text-center text-sm text-neutral-400">
                Please <span className="text-[#00F0FF] cursor-pointer hover:underline" onClick={() => toast.info('Please sign in to subscribe')}>sign in</span> to subscribe
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Share-this-product modal */}
      <ShareProductModal
        open={showShareModal}
        onOpenChange={setShowShareModal}
        product={product}
        creator={creator}
        user={user}
      />

    </div>
  );
}

export default ProductDetail;
